/**
 * Dashboard KPI Cards Component
 * Displays 4 key performance indicator cards
 */

import { useMemo } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { TrendingUp, TrendingDown } from 'lucide-react'
import type { DashboardMetrics, TeamHoursResponse } from '@/types/ui/dashboard'
import { useFormatters } from '@/hooks/use-formatters'
import { cn } from '@/lib/utils'

interface DashboardKPICardsProps {
  metrics: DashboardMetrics
  teamHoursData: TeamHoursResponse | null
}

export function DashboardKPICards({ metrics, teamHoursData }: DashboardKPICardsProps) {
  const { formatMoney, formatPercent } = useFormatters()

  // Calculate totals from team hours data
  const hoursMetrics = useMemo(() => {
    if (!teamHoursData || teamHoursData.teamMembers.length === 0) {
      return { billable: 0, nonBillable: 0, total: 0, billablePercent: 0 }
    }

    const totalBillable = teamHoursData.teamMembers.reduce((sum, m) => sum + m.billable, 0)
    const totalNonBillable = teamHoursData.teamMembers.reduce((sum, m) => sum + m.nonBillable, 0)
    const totalActual = teamHoursData.teamMembers.reduce((sum, m) => sum + m.actual, 0)
    const billablePercent = totalActual > 0 ? (totalBillable / totalActual) * 100 : 0

    return {
      billable: totalBillable,
      nonBillable: totalNonBillable,
      total: totalActual,
      billablePercent,
    }
  }, [teamHoursData])

  return (
    <div className="grid grid-cols-4 gap-4">
      {/* Contract Amount */}
      <Card>
        <CardContent className="p-3">
          <p className="text-xs text-muted-foreground mb-0.5">Contract</p>
          <p className="text-lg font-bold">{formatMoney(metrics.contractAmount)}</p>
        </CardContent>
      </Card>

      {/* Revenue to Date */}
      <Card>
        <CardContent className="p-3">
          <p className="text-xs text-muted-foreground mb-0.5">Revenue to date</p>
          <p className="text-lg font-bold mb-0.5">{formatMoney(metrics.revenueToDate)}</p>
          {metrics.revenueTrend !== null && (
            <div className="flex items-center gap-1">
              {metrics.revenueTrend >= 0 ? (
                <>
                  <TrendingUp className="w-3 h-3 text-green-600" />
                  <span className="text-xs text-green-600">
                    +{formatPercent(metrics.revenueTrend, 0)} from last month
                  </span>
                </>
              ) : (
                <>
                  <TrendingDown className="w-3 h-3 text-red-600" />
                  <span className="text-xs text-red-600">
                    {formatPercent(metrics.revenueTrend, 0)} from last month
                  </span>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Gross Margin */}
      <Card>
        <CardContent className="p-3">
          <p className="text-xs text-muted-foreground mb-0.5">Gross Margin</p>
          <div className="flex items-baseline gap-2">
            <p className="text-lg font-bold">{formatPercent(metrics.grossMarginPercent, 0)}</p>
            <Badge
              className={cn(
                "text-xs h-4 px-1.5",
                metrics.grossMarginPercent < 0
                  ? "bg-red-100 text-red-700 hover:bg-red-100"
                  : metrics.grossMarginPercent < 20
                    ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-100"
                    : "bg-green-100 text-green-700 hover:bg-green-100"
              )}
            >
              {metrics.grossMarginPercent < 0
                ? 'Negative'
                : metrics.grossMarginPercent < 20
                  ? 'Warning'
                  : 'Healthy'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Billable vs Non-Billable Hours */}
      <Card>
        <CardContent className="p-3">
          <p className="text-xs text-muted-foreground mb-2">Billable vs Non-Billable Hours</p>
          <div className="flex items-center justify-center">
            {/* Circular Progress */}
            <div className="relative w-20 h-20">
              <svg className="w-full h-full -rotate-90">
                <circle 
                  cx="40" 
                  cy="40" 
                  r="35" 
                  stroke="hsl(var(--muted))" 
                  strokeWidth="7" 
                  fill="none" 
                />
                <circle
                  cx="40"
                  cy="40"
                  r="35"
                  stroke="#60a5fa"
                  strokeWidth="7"
                  fill="none"
                  strokeDasharray={`${(hoursMetrics.billablePercent / 100) * 219.9} 219.9`}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-base font-bold">{formatPercent(hoursMetrics.billablePercent, 0)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

